import { useCallback } from 'react';
import { logError } from 'utils/logger';
import { useMutation, useQuery } from '@apollo/client';
import { QuoteFormValues } from '../types';
import { CREATE_OR_UPDATE_QUOTE, GET_QUOTE_BY_VO_ID } from '../api';
import { createOrUpdateQuoteDTOSchema } from '../model/schema';
import {
  CreateOrUpdateQuoteVariables,
  CreateOrUpdateQuoteResponse,
  GetQuoteResponse,
  GetQuoteVariables,
} from '../api/types';

export function useQuote(voId?: string) {
  const {
    error,
    data: quoteResponse,
    loading: isQuoteLoading,
  } = useQuery<GetQuoteResponse, GetQuoteVariables>(GET_QUOTE_BY_VO_ID, {
    skip: !voId,
    notifyOnNetworkStatusChange: true,
    variables: { vendorOpportunityId: voId! },
  });

  const [onUpdate, { loading }] = useMutation<CreateOrUpdateQuoteResponse, CreateOrUpdateQuoteVariables>(
    CREATE_OR_UPDATE_QUOTE
  );

  const handleCreateOrUpdateQuote = useCallback(
    async (voId: string, updateDTO: QuoteFormValues) => {
      try {
        const collectDTO: CreateOrUpdateQuoteVariables['input'] = { vendorOpportunityId: voId, ...updateDTO };

        const { success, error, data } = createOrUpdateQuoteDTOSchema.safeParse(collectDTO);

        if (success) {
          await onUpdate({ variables: { input: data } });
        } else {
          logError('handleUpdateQuote input parse failed', error);
        }
      } catch (error) {
        logError(error);
      }
    },
    [onUpdate]
  );

  return {
    error,
    isQuoteLoading,
    handleCreateOrUpdateQuote,
    isQuoteCreatingOrUpdating: loading,
    quote: quoteResponse?.vendorOpportunity?.quote ?? null,
  };
}
